import React, { useState, useEffect, useMemo } from 'react';

import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import FilterListIcon from '@mui/icons-material/FilterList';
import SaveIcon from '@mui/icons-material/Save';
import SearchIcon from '@mui/icons-material/Search';
import Alert from '@mui/material/Alert';
import Box from '@mui/material/Box';
import Button from '@mui/material/Button';
import Card from '@mui/material/Card';
import CardContent from '@mui/material/CardContent';
import CircularProgress from '@mui/material/CircularProgress';
import Divider from '@mui/material/Divider';
import InputAdornment from '@mui/material/InputAdornment';
import List from '@mui/material/List';
import ListItem from '@mui/material/ListItem';
import ListItemButton from '@mui/material/ListItemButton';
import Menu from '@mui/material/Menu';
import MenuItem from '@mui/material/MenuItem';
import Paper from '@mui/material/Paper';
import TextField from '@mui/material/TextField';
import Typography from '@mui/material/Typography';

import CreateTicketDialog from 'components/common/CreateTicketDialog';
import useCurrentUser from 'hooks/useCurrentUser';
import {
  useGetPromptSectionsQuery,
  useCreatePromptSectionMutation,
  useSuggestEditPromptSectionMutation,
} from 'services/api/promptSections.api';
import { PromptSection } from 'services/types/promptSection';

interface Props {
  businessUnitId: number;
  refetchFlag?: number;
}

type FilterType = 'all' | 'editable' | 'read-only' | 'suggest-edit';

const PromptBuilder: React.FC<Props> = ({ businessUnitId, refetchFlag }) => {
  const {
    data: promptSections,
    isLoading: loadingSections,
    refetch: refetchSections,
  } = useGetPromptSectionsQuery({ businessUnitId });
  const [createPromptSection] = useCreatePromptSectionMutation();
  const [suggestEditPromptSection] = useSuggestEditPromptSectionMutation();

  const currentUser = useCurrentUser();

  // UI State
  const [searchTerm, setSearchTerm] = useState('');
  const [filterType, setFilterType] = useState<FilterType>('all');
  const [filterMenuAnchor, setFilterMenuAnchor] = useState<null | HTMLElement>(null);
  const [selectedSection, setSelectedSection] = useState<PromptSection | null>(null);
  const [editedValue, setEditedValue] = useState('');
  const [isEditing, setIsEditing] = useState(false);
  const [isSaving, setIsSaving] = useState(false);
  const [openCreateTicketDialog, setOpenCreateTicketDialog] = useState(false);

  const availableSections = promptSections?.filter((section) => section.isLastVersion && !section.isSuggestion) || [];

  const filteredSections = useMemo(() => {
    let filtered = availableSections;

    if (searchTerm) {
      filtered = filtered.filter(
        (section) =>
          section.displayName.toLowerCase().includes(searchTerm.toLowerCase()) ||
          section.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          section.value.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    if (filterType !== 'all') {
      filtered = filtered.filter((section) => {
        if (filterType === 'editable') return section.permission === 'editable';
        if (filterType === 'read-only') return section.permission === 'read-only';
        if (filterType === 'suggest-edit') return section.permission === 'suggest-edit';
        return true;
      });
    }

    return filtered;
  }, [availableSections, searchTerm, filterType]);

  useEffect(() => {
    if (refetchFlag) {
      // eslint-disable-next-line no-console
      refetchSections().catch(console.error);
    }
  }, [refetchFlag, refetchSections]);

  useEffect(() => {
    if (selectedSection) {
      setEditedValue(selectedSection.value);
      setIsEditing(false);
    }
  }, [selectedSection]);

  const handleSectionSelect = (section: PromptSection) => {
    if (isEditing && editedValue !== selectedSection?.value) {
      // eslint-disable-next-line no-alert
      const confirmDiscard = window.confirm('Tienes cambios sin guardar. ¿Deseas descartarlos?');
      if (!confirmDiscard) return;
    }
    setSelectedSection(section);
  };

  const handleStartEdit = () => {
    setIsEditing(true);
  };

  const handleCancelEdit = () => {
    setEditedValue(selectedSection?.value || '');
    setIsEditing(false);
  };

  const handleSaveChanges = async () => {
    if (!selectedSection) return;

    setIsSaving(true);
    const isEditable = selectedSection.permission === 'editable';

    try {
      if (isEditable) {
        await createPromptSection({
          businessUnitId,
          promptSection: {
            name: selectedSection.name,
            displayName: selectedSection.displayName,
            value: editedValue,
            permission: selectedSection.permission,
            businessUnitId,
          },
        }).unwrap();
      } else {
        await suggestEditPromptSection({
          businessUnitId,
          promptSection: {
            name: selectedSection.name,
            displayName: selectedSection.displayName,
            value: editedValue,
          },
        }).unwrap();
      }

      await refetchSections();
      setIsEditing(false);

      const updatedSections = await refetchSections().unwrap();
      const updatedSection = updatedSections?.find(
        (s) => s.name === selectedSection.name && s.isLastVersion && !s.isSuggestion
      );
      if (updatedSection) {
        setSelectedSection(updatedSection);
      }
    } catch (error) {
      // eslint-disable-next-line no-console
      console.error('Error saving section:', error);
      // eslint-disable-next-line no-alert
      alert('Error al guardar los cambios');
    } finally {
      setIsSaving(false);
    }
  };

  const handleTicketCreated = () => {
    setOpenCreateTicketDialog(false);
  };

  const getFilterLabel = () => {
    if (filterType === 'all') return 'Todos';
    if (filterType === 'editable') return 'Editables';
    if (filterType === 'read-only') return 'Solo Lectura';
    if (filterType === 'suggest-edit') return 'Sugerir Cambio';
    return 'Todos';
  };

  const shouldShowTicketButton = currentUser && !currentUser.isAdmin && currentUser.role === 'businessUnitAdmin';

  if (loadingSections) {
    return (
      <Box display="flex" justifyContent="center" alignItems="center" height="400px">
        <CircularProgress size={60} />
      </Box>
    );
  }

  return (
    <Box sx={{ height: 'calc(100vh - 120px)', p: 3, display: 'flex', flexDirection: 'column' }}>
      {/* Toolbar */}
      <Paper elevation={1} sx={{ p: 2, mb: 3, borderRadius: 2 }}>
        <Box display="flex" gap={2} alignItems="center" flexWrap="wrap">
          <TextField
            placeholder="Buscar secciones..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            size="small"
            sx={{ minWidth: 300, flex: 1 }}
            InputProps={{
              startAdornment: (
                <InputAdornment position="start">
                  <SearchIcon color="action" />
                </InputAdornment>
              ),
            }}
          />

          <Button
            variant="outlined"
            startIcon={<FilterListIcon />}
            onClick={(e) => setFilterMenuAnchor(e.currentTarget)}
            size="small"
            sx={{ textTransform: 'none' }}
          >
            {getFilterLabel()}
          </Button>

          {shouldShowTicketButton && (
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={() => setOpenCreateTicketDialog(true)}
              sx={{ textTransform: 'none' }}
            >
              Nuevo Ticket
            </Button>
          )}

          <Typography variant="body2" color="text.secondary">
            {filteredSections.length} de {availableSections.length} secciones
          </Typography>
        </Box>
      </Paper>

      {/* Main Content */}
      <Box display="flex" gap={3} sx={{ flex: 1, minHeight: 0 }}>
        {/* Left Panel - Sections List */}
        <Paper
          elevation={2}
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
          }}
        >
          <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
            <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
              Secciones de Prompt
            </Typography>
          </Box>

          <Box sx={{ flex: 1, overflow: 'auto' }}>
            {filteredSections.length === 0 ? (
              <Box sx={{ p: 4, textAlign: 'center' }}>
                <Typography variant="body1" color="text.secondary" mb={2}>
                  {searchTerm || filterType !== 'all'
                    ? 'No se encontraron secciones con los filtros aplicados'
                    : 'No hay secciones disponibles'}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  {searchTerm || filterType !== 'all'
                    ? 'Intenta cambiar los filtros de búsqueda'
                    : 'No hay secciones para mostrar'}
                </Typography>
              </Box>
            ) : (
              <List sx={{ p: 0 }}>
                {filteredSections.map((section, index) => {
                  const isSelected = selectedSection?.id === section.id;

                  return (
                    <React.Fragment key={section.id}>
                      <ListItem sx={{ p: 0 }}>
                        <ListItemButton
                          onClick={() => handleSectionSelect(section)}
                          selected={isSelected}
                          sx={{
                            py: 2,
                            px: 2,
                            flexDirection: 'column',
                            alignItems: 'flex-start',
                            gap: 1,
                            '&.Mui-selected': {
                              backgroundColor: '#e3f2fd',
                              borderRight: '4px solid #1976d2',
                            },
                          }}
                        >
                          <Box display="flex" justifyContent="space-between" alignItems="center" width="100%">
                            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: '#1976d2' }}>
                              {section.displayName}
                            </Typography>
                          </Box>

                          <Typography
                            variant="body2"
                            sx={{
                              color: '#555',
                              fontSize: '0.8rem',
                              overflow: 'hidden',
                              display: '-webkit-box',
                              WebkitLineClamp: 2,
                              WebkitBoxOrient: 'vertical',
                              width: '100%',
                            }}
                          >
                            {section.value}
                          </Typography>
                        </ListItemButton>
                      </ListItem>
                      {index < filteredSections.length - 1 && <Divider />}
                    </React.Fragment>
                  );
                })}
              </List>
            )}
          </Box>
        </Paper>

        {/* Right Panel - Section Editor */}
        <Paper
          elevation={2}
          sx={{
            width: '50%',
            height: '100%',
            display: 'flex',
            flexDirection: 'column',
            borderRadius: 2,
          }}
        >
          {selectedSection ? (
            <>
              <Box sx={{ p: 2, borderBottom: '1px solid #e0e0e0' }}>
                <Box display="flex" justifyContent="space-between" alignItems="center" mb={1}>
                  <Typography variant="h6" color="primary" sx={{ fontWeight: 600 }}>
                    {selectedSection.displayName}
                  </Typography>
                  <Box display="flex" gap={1}>
                    {selectedSection.permission === 'editable' && !isEditing && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleStartEdit}
                        sx={{ textTransform: 'none' }}
                      >
                        Editar
                      </Button>
                    )}
                    {selectedSection.permission !== 'editable' && !isEditing && (
                      <Button
                        variant="outlined"
                        size="small"
                        startIcon={<EditIcon />}
                        onClick={handleStartEdit}
                        sx={{ textTransform: 'none' }}
                      >
                        Sugerir Cambio
                      </Button>
                    )}
                  </Box>
                </Box>

                {selectedSection.permission !== 'editable' && isEditing && (
                  <Alert severity="info" sx={{ mt: 2 }}>
                    Los cambios se enviarán como sugerencia para revisión por un administrador.
                  </Alert>
                )}
              </Box>

              <Box sx={{ flex: 1, p: 2, display: 'flex', flexDirection: 'column' }}>
                {isEditing ? (
                  <>
                    <TextField
                      multiline
                      fullWidth
                      value={editedValue}
                      onChange={(e) => setEditedValue(e.target.value)}
                      placeholder="Escribe el contenido de la sección..."
                      sx={{
                        flex: 1,
                        '& .MuiInputBase-root': {
                          height: '100%',
                          alignItems: 'flex-start',
                          fontFamily: 'monospace',
                          fontSize: '0.9rem',
                        },
                        '& .MuiInputBase-input': {
                          height: '100% !important',
                          overflow: 'auto !important',
                          resize: 'none',
                        },
                      }}
                    />

                    <Box display="flex" gap={2} mt={2}>
                      <Button
                        variant="contained"
                        startIcon={<SaveIcon />}
                        onClick={() => {
                          handleSaveChanges().catch(console.error); // eslint-disable-line no-console
                        }}
                        disabled={isSaving || editedValue === selectedSection.value}
                        sx={{ textTransform: 'none' }}
                      >
                        {isSaving ? 'Guardando...' : 'Guardar Cambios'}
                      </Button>
                      <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        disabled={isSaving}
                        sx={{ textTransform: 'none' }}
                      >
                        Cancelar
                      </Button>
                    </Box>
                  </>
                ) : (
                  <Card variant="outlined" sx={{ flex: 1, backgroundColor: '#f8f9fa' }}>
                    <CardContent>
                      <Typography
                        variant="body1"
                        sx={{
                          fontFamily: 'monospace',
                          whiteSpace: 'pre-wrap',
                          lineHeight: 1.6,
                          fontSize: '0.9rem',
                          color: '#333',
                        }}
                      >
                        {selectedSection.value}
                      </Typography>
                    </CardContent>
                  </Card>
                )}
              </Box>
            </>
          ) : (
            <Box sx={{ flex: 1, display: 'flex', alignItems: 'center', justifyContent: 'center', p: 4 }}>
              <Box textAlign="center">
                <Typography variant="h6" color="text.secondary" mb={2}>
                  Selecciona una sección
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Haz clic en una sección de la lista para ver y editar su contenido
                </Typography>
              </Box>
            </Box>
          )}
        </Paper>
      </Box>

      {/* Filter Menu */}
      <Menu anchorEl={filterMenuAnchor} open={Boolean(filterMenuAnchor)} onClose={() => setFilterMenuAnchor(null)}>
        <MenuItem
          onClick={() => {
            setFilterType('all');
            setFilterMenuAnchor(null);
          }}
        >
          Todas las secciones
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterType('editable');
            setFilterMenuAnchor(null);
          }}
        >
          Solo editables
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterType('read-only');
            setFilterMenuAnchor(null);
          }}
        >
          Solo lectura
        </MenuItem>
        <MenuItem
          onClick={() => {
            setFilterType('suggest-edit');
            setFilterMenuAnchor(null);
          }}
        >
          Sugerir Cambio
        </MenuItem>
      </Menu>

      {/* Create Ticket Dialog */}
      <CreateTicketDialog
        open={openCreateTicketDialog}
        onClose={() => setOpenCreateTicketDialog(false)}
        businessUnitId={businessUnitId}
        isAdmin={false}
        onSuccess={handleTicketCreated}
      />
    </Box>
  );
};

PromptBuilder.defaultProps = {
  refetchFlag: 0,
};

export default PromptBuilder;
